import { componentText, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { styleMap } from 'lit/directives/style-map.js'
import { computePosition, flip, offset, shift, type Placement } from '@floating-ui/dom'
import {
	animationFrameScheduler,
	auditTime,
	catchError,
	EMPTY,
	filter,
	finalize,
	from,
	fromEvent,
	map,
	merge,
	Observable,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs'
import { SPRING_SMOOTH, SPRING_SNAPPY } from '@schmancy/utils/animation'
import { cursorGlow } from '@schmancy/directives/cursor-glow'
import { reducedMotion$ } from '@schmancy/directives/reduced-motion'
import { theme } from '@schmancy/theme/theme.service'
import type { SnapCorner, WindowBounds, WindowVisualState } from './window-registry.js'
import { windowManager } from './window-manager.js'
import { anchorOriginVars, surfaceAnimation } from '@schmancy/overlay/overlay.animations'
import type { WindowRecord } from './window-registry.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import '../button/button'
import '../layout/scroll/scroll'
import '../surface/surface'

const HEAD_HEIGHT = 48
const DRAG_THRESHOLD = 5
/** The container's rounded-2xl corner, spelled as the ladder token so the
 *  clip-path masks below move with radius.setBase() instead of freezing. */
const WINDOW_CORNER = 'var(--schmancy-radius-2xl, 1.25rem)'

interface Position {
	x: number
	y: number
}

@customElement('schmancy-window')
export default class SchmancyWindow extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: contents;
				position: relative;
				z-index: 1000;
			}
			:host([hidden]) {
				display: none !important;
			}
		`,
	]

	@property({ type: Boolean, reflect: true }) override hidden = false
	@property({ type: String }) label = ''
	/** Keep the docked tab strip visible even with one member. */
	@property({ type: Boolean }) tabbed = false
	@property({ type: String }) id: string = 'default'
	/** e.g. '320px', '24rem'. Defaults to responsive sizing. */
	@property({ type: String }) expandedWidth?: string
	/** e.g. '400px', '50vh'. */
	@property({ type: String }) expandedHeight?: string
	/** Lower elevation shadow while collapsed. */
	@property({ type: Boolean, reflect: true }) lowered: boolean = false
	@property({ type: String }) corner: SnapCorner = 'bottom-right'
	@property({ type: Boolean }) resizable = false
	/** Skips corner snapping — stays wherever dragged. */
	@property({ type: Boolean }) freePosition = false
	@property({ type: String, reflect: true }) visualState: WindowVisualState = 'normal'
	/** Pixels. */
	@property({ type: Number }) minWidth = 280
	/** Pixels. */
	@property({ type: Number }) minHeight = 200

	@property({ type: Boolean, reflect: true }) open = false

	private currentAnimation?: Animation
	private snapAnimation?: Animation
	private isDocked = true
	private hadFocus = false

	/** Lazy-mounts body content on first expand. */
	@state() private hasOpened = false

	// Plain field, not @state — mutated directly during drag without a re-render
	private position: Position = { x: 16, y: 16 }
	@state() private currentCorner: SnapCorner = 'bottom-right'
	@state() private cornerMembers: WindowRecord[] = []

	// Avoids unnecessary style.removeProperty calls during drag
	private appliedCorner: string = ''

	private containerRef = createRef<HTMLElement>()
	private bodyRef = createRef<HTMLElement>()
	private headRef = createRef<HTMLElement>()

	// ============================================
	// COMPUTED
	// ============================================

	private get panelWidth(): string {
		return this.expandedWidth ?? 'min(360px, calc(100vw - 32px))'
	}

	private get isBottomCorner(): boolean {
		return this.currentCorner.startsWith('bottom')
	}

	// Both masks round at the container's own rung — the same corner its
	// rounded-2xl class paints — so the collapsed head, the animation, and the
	// resting panel are one shape that follows radius.setBase() together.
	private get closedClipPath(): string {
		return this.isBottomCorner && !this.tabbed && this.cornerMembers.length < 2
			? `inset(calc(100% - ${HEAD_HEIGHT}px) 0px 0px 0px round ${WINDOW_CORNER})`
			: `inset(0px 0px calc(100% - ${HEAD_HEIGHT}px) 0px round ${WINDOW_CORNER})`
	}

	private get openClipPath(): string {
		return `inset(0px 0px 0px 0px round ${WINDOW_CORNER})`
	}

	// ============================================
	// POSITION MANAGEMENT
	// ============================================

	private applyContainerPosition() {
		const container = this.containerRef.value
		if (!container) return
		// Only clear position properties when corner changes (avoids 4 style invalidations per drag frame)
		if (this.appliedCorner !== this.currentCorner) {
			container.style.removeProperty('left')
			container.style.removeProperty('right')
			container.style.removeProperty('top')
			container.style.removeProperty('bottom')
			this.appliedCorner = this.currentCorner
		}
		const { x, y } = this.position
		if (this.currentCorner.includes('right')) {
			container.style.right = `${x}px`
		} else {
			container.style.left = `${x}px`
		}
		if (this.currentCorner.includes('bottom')) {
			container.style.bottom = `${y + theme.bottomOffset}px`
		} else {
			container.style.top = `${y}px`
		}
	}

	/** Re-anchors near a trigger's rect every time it's called — an explicit
	 *  reopen always re-anchors; only a live drag while open takes it elsewhere. */
	async anchorToRect(rect: DOMRectReadOnly) {
		const container = this.containerRef.value
		if (!container) return
		const reference = { getBoundingClientRect: () => rect }
		const isRight = this.currentCorner.includes('right')
		const isBottom = this.currentCorner.includes('bottom')
		const placement: Placement = isBottom ? (isRight ? 'top-end' : 'top-start') : isRight ? 'bottom-end' : 'bottom-start'
		const { x, y } = await computePosition(reference, container, {
			strategy: 'fixed',
			placement,
			middleware: [offset(8), flip(), shift({ padding: 16 })],
		})
		const box = container.getBoundingClientRect()
		this.position = {
			x: isRight ? window.innerWidth - x - box.width : x,
			y: isBottom ? window.innerHeight - theme.bottomOffset - y - box.height : y,
		}
		this.applyContainerPosition()
	}

	private static readonly VALID_CORNERS = new Set<string>(['top-left', 'top-right', 'bottom-left', 'bottom-right'])

	private loadPosition() {
		const saved = windowManager.loadPosition(this.id)
		if (saved) {
			this.position = { x: saved.x, y: saved.y }
			this.isDocked = saved.snapTarget !== 'free'
			if (SchmancyWindow.VALID_CORNERS.has(saved.anchor)) {
				this.currentCorner = saved.anchor as SnapCorner
			}
		}
	}

	private savePosition() {
		windowManager.savePosition(this.id, {
			...this.position,
			anchor: this.currentCorner,
			snapTarget: this.isDocked && !this.freePosition ? this.currentCorner : 'free',
		})
	}

	private validateBounds() {
		const container = this.containerRef.value
		if (!container) return
		const rect = container.getBoundingClientRect()
		if (rect.width === 0) return
		const vw = window.innerWidth
		const vh = window.innerHeight
		const isRight = this.currentCorner.includes('right')
		const isBottom = this.currentCorner.includes('bottom')
		const actualLeft = isRight ? vw - this.position.x - rect.width : this.position.x
		const actualTop = isBottom ? vh - theme.bottomOffset - this.position.y - rect.height : this.position.y
		const newLeft = Math.max(0, Math.min(actualLeft, vw - rect.width))
		const newTop = Math.max(0, Math.min(actualTop, vh - theme.bottomOffset - rect.height))
		this.position = {
			x: isRight ? vw - newLeft - rect.width : newLeft,
			y: isBottom ? vh - theme.bottomOffset - newTop - rect.height : newTop,
		}
		this.applyContainerPosition()
	}

	// ============================================
	// CORNER SNAPPING
	// ============================================

	private reorientToNearestCorner(skipAnimation = false): void {
		const container = this.containerRef.value
		const head = this.headRef.value
		if (!container || !head) return
		const rect = container.getBoundingClientRect()
		const headRect = head.getBoundingClientRect()
		const side = headRect.left <= 32 ? 'left' : window.innerWidth - headRect.right <= 32 ? 'right' : null
		const vert =
			headRect.top <= 32 ? 'top' : window.innerHeight - theme.bottomOffset - headRect.bottom <= 32 ? 'bottom' : null
		if (this.freePosition || !side || !vert) {
			this.isDocked = false
			this.savePosition()
			windowManager.updateSnapTarget(this.id, 'free')
			windowManager.updateBounds(this.id, { left: rect.left, top: rect.top, width: rect.width, height: rect.height })
			return
		}
		const newCorner: SnapCorner = `${vert}-${side}`
		this.isDocked = true
		windowManager.updateSnapTarget(this.id, newCorner)

		this.currentCorner = newCorner
		// Standard 16px edge gap
		this.position = { x: 16, y: 16 }
		this.applyContainerPosition()
		if (!this.open) {
			container.style.clipPath = this.closedClipPath
		}

		if (skipAnimation || reducedMotion$.value) {
			this.savePosition()
			const snapRect = container.getBoundingClientRect()
			windowManager.updateBounds(this.id, {
				left: snapRect.left,
				top: snapRect.top,
				width: snapRect.width,
				height: snapRect.height,
			})
			return
		}

		// I -- invert: shift element back to its original visual position
		const newRect = container.getBoundingClientRect()
		const dx = rect.left - newRect.left
		const dy = rect.top - newRect.top
		container.style.translate = `${dx}px ${dy}px`

		// P -- play: animate from the inverse offset to natural resting position
		const flipKeyframes: Keyframe[] = [{ translate: `${dx}px ${dy}px` }, { translate: '0px 0px' }]
		const anim = container.animate(flipKeyframes, {
			duration: SPRING_SMOOTH.duration,
			easing: SPRING_SMOOTH.easingFallback,
			fill: 'forwards',
		})
		this.snapAnimation = anim
		from(anim.finished)
			.pipe(
				take(1),
				tap(() => {
					if (container.isConnected) container.style.translate = ''
					anim.cancel()
					this.snapAnimation = undefined
					const finalRect = container.getBoundingClientRect()
					windowManager.updateBounds(this.id, {
						left: finalRect.left,
						top: finalRect.top,
						width: finalRect.width,
						height: finalRect.height,
					})
				}),
				catchError(() => EMPTY),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.savePosition()
	}

	// ============================================
	// DRAG PIPELINE
	// ============================================

	private drag$(): Observable<never> {
		return new Observable(() => {
			const head = this.headRef.value
			const container = this.containerRef.value
			if (!head || !container) return

			let didDrag = false

			const sub = fromEvent<PointerEvent>(head, 'pointerdown')
				.pipe(
					filter(e => e.button === 0),
					filter(e => {
						if (!(e.target instanceof Element)) return false
						if (e.target.closest('button[role="tab"][aria-selected="true"]')) return true
						return !e.target.closest('input, textarea, select, schmancy-input, schmancy-icon-button, button, a')
					}),
					tap(e => {
						e.preventDefault()
						e.stopPropagation()
						windowManager.focus(this.id)
					}),
				)
				.pipe(
					map(e => {
						const rect = container.getBoundingClientRect()
						const isBottom = this.currentCorner.includes('bottom')
						if (this.snapAnimation) {
							this.snapAnimation.cancel()
							this.snapAnimation = undefined
							container.style.translate = ''
							this.position = {
								x: this.currentCorner.includes('right') ? window.innerWidth - rect.right : rect.left,
								y: isBottom ? window.innerHeight - rect.bottom - theme.bottomOffset : rect.top,
							}
							this.applyContainerPosition()
						}
						const wasOpen = this.open
						didDrag = false
						return {
							startX: e.clientX,
							startY: e.clientY,
							offsetX: e.clientX - rect.left,
							offsetY: e.clientY - rect.top,
							rect,
							vw: window.innerWidth,
							vh: window.innerHeight,
							isBottom,
							wasOpen,
							fromTab:
								e.target instanceof Element && Boolean(e.target.closest('button[role="tab"][aria-selected="true"]')),
							pointerId: e.pointerId,
						}
					}),
					switchMap(({ startX, startY, offsetX, offsetY, rect, vw, vh, isBottom, wasOpen, fromTab, pointerId }) => {
						const move$ = fromEvent<PointerEvent>(window, 'pointermove').pipe(
							filter(e => e.pointerId === pointerId),
							map(e => ({ clientX: e.clientX, clientY: e.clientY })),
						)
						let ended: string | undefined
						const end$ = merge(
							fromEvent<PointerEvent>(window, 'pointerup'),
							fromEvent<PointerEvent>(window, 'pointercancel'),
						).pipe(
							filter(e => e.pointerId === pointerId),
							tap(e => {
								ended = e.type
								if (e.type === 'pointerup' && didDrag) {
									const left = Math.max(0, Math.min(e.clientX - offsetX, vw - rect.width))
									const top = Math.max(
										isBottom ? HEAD_HEIGHT - rect.height : 0,
										Math.min(
											e.clientY - offsetY,
											isBottom ? vh - theme.bottomOffset - rect.height : vh - theme.bottomOffset - HEAD_HEIGHT,
										),
									)
									this.position = {
										x: this.currentCorner.includes('right') ? vw - left - rect.width : left,
										y: isBottom ? vh - theme.bottomOffset - top - rect.height : top,
									}
									this.applyContainerPosition()
								}
							}),
						)

						return move$.pipe(
							tap(({ clientX, clientY }) => {
								const dx = clientX - startX
								const dy = clientY - startY
								if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD && !didDrag) {
									didDrag = true
									this.currentAnimation?.cancel()
									this.currentAnimation = undefined
									container.style.willChange = ''
									this.isDocked = false
									windowManager.updateSnapTarget(this.id, 'free')
									windowManager.updateOpen(this.id, false)
									this.applyDragVisuals(true)
									if (wasOpen) {
										this.open = false
										container.style.clipPath = this.closedClipPath
										container.style.overflow = 'hidden'
										const body = this.bodyRef.value
										if (body) {
											body.inert = true
											body.style.visibility = 'hidden'
										}
									}
								}
								if (!didDrag) return

								const left = Math.max(0, Math.min(clientX - offsetX, vw - rect.width))
								const minTop = isBottom ? HEAD_HEIGHT - rect.height : 0
								const maxTop = isBottom ? vh - theme.bottomOffset - rect.height : vh - theme.bottomOffset - HEAD_HEIGHT
								const top = Math.max(minTop, Math.min(clientY - offsetY, maxTop))

								this.position = {
									x: this.currentCorner.includes('right') ? vw - left - rect.width : left,
									y: isBottom ? vh - theme.bottomOffset - top - rect.height : top,
								}
								this.applyContainerPosition()
							}),
							takeUntil(end$),
							finalize(() => {
								if (didDrag) {
									if (ended) {
										this.reorientToNearestCorner()
										if (wasOpen) this.expand()
									}
									this.applyDragVisuals(false)
									didDrag = false
								} else {
									didDrag = false
									if (ended === 'pointerup' && !fromTab) this.toggle()
								}
							}),
						)
					}),
				)
				.subscribe()

			return () => sub.unsubscribe()
		})
	}

	// ============================================
	// LIFECYCLE
	// ============================================

	connectedCallback() {
		super.connectedCallback()

		from(this.updateComplete)
			.pipe(
				take(1),
				tap(() => {
					this.currentCorner = this.corner
					this.loadPosition()
					this.applyContainerPosition()
					this.initDOMState()
					const container = this.containerRef.value
					if (container) {
						const rect = container.getBoundingClientRect()
						const bounds: WindowBounds = { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
						windowManager.register(
							this.id,
							bounds,
							this.freePosition || !this.isDocked ? 'free' : this.currentCorner,
							this.label ||
								this.querySelector(
									':scope > :not([slot]) schmancy-typography, :scope > schmancy-typography, :scope > [slot=header]',
								)?.textContent?.trim() ||
								this.querySelector(':scope > :not([slot])')?.textContent?.trim() ||
								'',
							this.hidden,
						)
						windowManager.updateOpen(this.id, this.open)
					}
				}),
				switchMap(() =>
					merge(
						this.drag$(),
						new Observable<void>(subscriber => {
							const observer = new MutationObserver(() => subscriber.next())
							observer.observe(this, { childList: true, subtree: true, characterData: true })
							return () => observer.disconnect()
						}).pipe(
							tap(() =>
								windowManager.updateLabel(
									this.id,
									this.label ||
										this.querySelector(
											':scope > :not([slot]) schmancy-typography, :scope > schmancy-typography, :scope > [slot=header]',
										)?.textContent?.trim() ||
										this.querySelector(':scope > :not([slot])')?.textContent?.trim() ||
										'',
								),
							),
						),
						windowManager.state$.pipe(
							tap(state => {
								const record = state.windows.get(this.id)
								const container = this.containerRef.value
								if (!record || !container) return
								this.cornerMembers =
									record.snapTarget === 'free'
										? []
										: [...state.windows.values()].filter(
												member => member.snapTarget === record.snapTarget && !member.hidden,
											)
								const active = [...this.cornerMembers].sort((a, b) => b.zIndex - a.zIndex)[0]
								const wasInactive = container.inert
								const gainsFocus = state.focusedId === this.id && !this.hadFocus
								this.hadFocus = state.focusedId === this.id
								container.style.zIndex = String(record.zIndex)
								container.style.display = active && active.id !== this.id ? 'none' : ''
								container.inert = Boolean(active && active.id !== this.id)
								if ((wasInactive || gainsFocus) && !container.inert && !record.hidden && state.focusedId === this.id) {
									this.headRef.value?.focus()
									void this.updateComplete.then(() =>
										this.headRef.value?.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]')?.focus(),
									)
								}
								if (record.open && !this.open && !record.hidden) this.expand()
							}),
						),
					),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		merge(
			fromEvent(window, 'resize').pipe(
				auditTime(0, animationFrameScheduler),
				tap(() => this.validateBounds()),
			),
			theme.bottomOffset$.pipe(tap(() => this.applyContainerPosition())),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	protected updated(changed: PropertyValues<this>) {
		if (changed.has('hidden')) {
			windowManager.updateHidden(this.id, this.hidden)
			if (!this.hidden) {
				windowManager.focus(this.id)
				// A window mounts hidden and is shown later, by which time the viewport
				// it last saved a position against (a prior session, a different screen)
				// may no longer fit it — unchecked until now, since only a 'resize' event
				// otherwise re-validates. Clamp before the user sees it, not after.
				this.validateBounds()
			}
		}
		if (changed.has('label') && this.label) windowManager.updateLabel(this.id, this.label)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.currentAnimation?.cancel()
		this.currentAnimation = undefined
		this.snapAnimation?.cancel()
		this.snapAnimation = undefined
		windowManager.unregister(this.id)
	}

	private initDOMState() {
		const container = this.containerRef.value
		const body = this.bodyRef.value
		if (!container) return

		this.applyContainerPosition()

		if (this.open) {
			this.hasOpened = true
			container.style.overflow = ''
			if (body) {
				body.inert = false
				body.style.visibility = 'visible'
			}
		} else {
			container.style.clipPath = this.closedClipPath
			container.style.overflow = 'hidden'
			if (body) {
				body.inert = true
				body.style.visibility = 'hidden'
			}
		}
	}

	// ============================================
	// ANIMATION
	// ============================================

	private animateOpen() {
		const container = this.containerRef.value
		const body = this.bodyRef.value
		if (!container) return
		this.currentAnimation?.cancel()
		this.currentAnimation = undefined

		this.hasOpened = true
		this.open = true

		windowManager.focus(this.id)
		windowManager.updateOpen(this.id, true)

		if (body) {
			body.style.visibility = 'visible'
			body.inert = false
		}

		if (reducedMotion$.value) {
			container.style.clipPath = ''
			container.style.overflow = ''
			this.dispatchScopedEvent('window-toggle', { state: 'expanded' })
			return
		}

		container.style.clipPath = ''
		container.style.overflow = 'hidden'
		container.style.willChange = 'transform, opacity'
		for (const [key, value] of Object.entries(
			anchorOriginVars(this.headRef.value, container.getBoundingClientRect()),
		)) {
			container.style.setProperty(key, value)
		}
		const animation = surfaceAnimation('anchored', 'in')
		const anim = container.animate(animation.keyframes, animation.options)
		this.currentAnimation = anim

		from(anim.finished)
			.pipe(
				take(1),
				tap(() => {
					if (container.isConnected) {
						container.style.clipPath = ''
						container.style.overflow = ''
						container.style.willChange = ''
					}
					anim.cancel()
					this.currentAnimation = undefined
					this.validateBounds()
				}),
				catchError(() => EMPTY),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.dispatchScopedEvent('window-toggle', { state: 'expanded' })
	}

	private animateClose() {
		const container = this.containerRef.value
		if (!container) return
		this.currentAnimation?.cancel()
		this.currentAnimation = undefined
		this.open = false
		windowManager.updateOpen(this.id, false)

		if (reducedMotion$.value) {
			container.style.clipPath = this.closedClipPath
			container.style.overflow = 'hidden'
			this.open = false
			const body = this.bodyRef.value
			if (body) {
				body.inert = true
				body.style.visibility = 'hidden'
			}
			this.dispatchScopedEvent('window-toggle', { state: 'collapsed' })
			return
		}

		container.style.overflow = 'hidden'
		container.style.willChange = 'opacity'
		const closeKeyframes: Keyframe[] = [
			{ clipPath: this.openClipPath, opacity: 1 },
			{ clipPath: this.closedClipPath, opacity: 0.95 },
		]
		const anim = container.animate(closeKeyframes, {
			duration: Math.round(SPRING_SNAPPY.duration * 0.7),
			easing: 'cubic-bezier(0.4, 0, 0.8, 0.15)',
			fill: 'forwards',
		})
		this.currentAnimation = anim

		from(anim.finished)
			.pipe(
				take(1),
				tap(() => {
					this.open = false
					container.style.clipPath = this.closedClipPath
					container.style.willChange = ''
					const body = this.bodyRef.value
					if (body) {
						body.inert = true
						body.style.visibility = 'hidden'
					}
					anim.cancel()
					this.currentAnimation = undefined
				}),
				catchError(() => EMPTY),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.dispatchScopedEvent('window-toggle', { state: 'collapsed' })
	}

	// ============================================
	// VISUAL STATE HELPERS
	// ============================================

	/** Direct DOM writes — skips a full Lit re-render for cursor/opacity. */
	private applyDragVisuals(dragging: boolean) {
		const head = this.headRef.value
		const container = this.containerRef.value
		if (head) {
			head.classList.toggle('cursor-grabbing', dragging)
			head.classList.toggle('cursor-move', !dragging)
		}
		if (container) {
			container.style.opacity = dragging ? '0.95' : ''
		}
	}

	private handleFocus = () => windowManager.focus(this.id)

	private handleHeadKeydown = (e: KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			this.toggle()
			return
		}
		if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
			e.preventDefault()
			const step = e.shiftKey ? 20 : 5
			const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0
			const dy = e.key === 'ArrowDown' ? step : e.key === 'ArrowUp' ? -step : 0
			this.position = {
				x: this.position.x + (this.currentCorner.includes('right') ? -dx : dx),
				y: this.position.y + (this.isBottomCorner ? -dy : dy),
			}
			this.isDocked = false
			windowManager.updateSnapTarget(this.id, 'free')
			this.applyContainerPosition()
			this.validateBounds()
			this.savePosition()
		}
	}

	// ============================================
	// PUBLIC API
	// ============================================

	toggle() {
		if (this.open) this.animateClose()
		else this.animateOpen()
	}

	expand() {
		if (this.open) {
			windowManager.focus(this.id)
			return
		}
		this.animateOpen()
	}

	close() {
		if (!this.open) return
		this.animateClose()
	}

	// ============================================
	// RENDER
	// ============================================

	protected render(): unknown {
		const tabs = this.tabbed || this.cornerMembers.length > 1
		const members: Pick<WindowRecord, 'id' | 'label'>[] = this.cornerMembers.length
			? this.cornerMembers
			: [{ id: this.id, label: this.label }]
		const isBottom = this.currentCorner.startsWith('bottom') && !tabs

		const containerClasses = classMap({
			fixed: true,
			flex: true,
			'flex-col': isBottom,
			'flex-col-reverse': !isBottom,
			'z-1000': true,
			'rounded-2xl': true,
			'overflow-hidden': true,
			'shadow-sm': !this.open && this.lowered,
			'shadow-md': !this.open && !this.lowered,
			'shadow-lg': this.open,
		})

		const containerStyles = styleMap({
			width: this.panelWidth,
			'max-height': 'calc(100vh - 32px)',
			'pointer-events': 'none',
		})

		const bodyStyles = styleMap({
			'pointer-events': this.open ? 'auto' : 'none',
		})

		const headClasses = classMap({
			'h-full': true,
			'px-3': !tabs,
			flex: true,
			'items-center': true,
			'gap-2': !tabs,
			'select-none': true,
			'cursor-move': true,
		})

		return html`
			<schmancy-surface
				${ref(this.containerRef)}
				type="container"
				opacity=${ifDefined(tabs ? 'solid' : undefined)}
				.elevated=${tabs}
				class=${containerClasses}
				style=${containerStyles}
				aria-expanded=${this.open}
				@pointerdown=${this.handleFocus}
			>
				<!-- Details section (visually above summary for bottom corners) -->
				<section
					${ref(this.bodyRef)}
					class=${classMap({ 'flex-1 min-h-0 overflow-hidden flex flex-col': true, 'bg-surface-container': tabs })}
					style=${bodyStyles}
					id="window-details"
					role=${tabs ? 'tabpanel' : 'region'}
					aria-label=${componentText('window.details')}
				>
					${this.hasOpened ? html`<slot name="details"></slot>` : nothing}
				</section>

				<!-- Summary — always interactive, unlike the lazy-mounted details section -->
				<section class="shrink-0 bg-surface-lowest pointer-events-auto h-12">
					<div
						${ref(this.headRef)}
						${cursorGlow({ radius: 200, intensity: 0.1 })}
						class=${headClasses}
						role=${tabs ? 'group' : 'button'}
						tabindex=${tabs ? -1 : 0}
						title=${componentText('window.moveHint')}
						aria-label=${componentText(tabs ? 'window.switcher' : this.open ? 'window.collapse' : 'window.expand')}
						@keydown=${this.handleHeadKeydown}
					>
						${when(
							tabs,
							() => html`
								<schmancy-scroll
									direction="horizontal"
									class="min-w-0 flex-1 h-full"
									label=${componentText('window.switcher')}
								>
									<div class="flex h-full items-stretch" role="tablist" aria-label=${componentText('window.switcher')}>
										${repeat(
											members,
											member => member.id,
											member => html`
												<div
													data-window-tab
													class=${classMap({
														'relative flex min-w-11 h-11 mt-1 max-w-48 shrink-0 items-center text-sm rounded-t-xl': true,
														'z-10 bg-surface-container text-surface-on': member.id === this.id,
														'bg-transparent text-surface-onVariant hover:bg-surface-containerLow':
															member.id !== this.id,
													})}
												>
													${when(
														member.id === this.id,
														() => html`
															<span
																data-tab-wing="left"
																aria-hidden="true"
																class="pointer-events-none absolute bottom-0 right-full size-[var(--radius-lg)] bg-[radial-gradient(circle_at_top_left,transparent_var(--radius-lg),var(--color-surface-container)_var(--radius-lg))]"
															></span>
															<span
																data-tab-wing="right"
																aria-hidden="true"
																class="pointer-events-none absolute bottom-0 left-full size-[var(--radius-lg)] bg-[radial-gradient(circle_at_top_right,transparent_var(--radius-lg),var(--color-surface-container)_var(--radius-lg))]"
															></span>
														`,
													)}
													<button
														type="button"
														role="tab"
														aria-selected=${member.id === this.id}
														aria-controls=${ifDefined(member.id === this.id ? 'window-details' : undefined)}
														tabindex=${member.id === this.id ? 0 : -1}
														class="flex min-w-0 flex-1 h-full items-center px-4 text-left cursor-pointer rounded-t-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-default focus-visible:-outline-offset-2"
														@keydown=${(event: KeyboardEvent) => {
															event.stopPropagation()
															const index = members.findIndex(item => item.id === member.id)
															let next = index
															if (event.key === 'ArrowRight') next = (index + 1) % members.length
															else if (event.key === 'ArrowLeft') next = (index - 1 + members.length) % members.length
															else if (event.key === 'Home') next = 0
															else if (event.key === 'End') next = members.length - 1
															else return
															event.preventDefault()
															windowManager.activate(members[next].id)
														}}
														@click=${() => windowManager.activate(member.id)}
													>
														<span class="truncate">${member.label || componentText('window.unnamed')}</span>
													</button>
													${when(
														member.id === this.id,
														() =>
															html` <button
																type="button"
																aria-label=${componentText('window.closeTab', { label: member.label || componentText('window.unnamed') })}
																class="flex size-6 pointer-coarse:size-11 mr-1 shrink-0 items-center justify-center rounded-full text-base text-surface-onVariant cursor-pointer hover:bg-surface-containerLow focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-default focus-visible:-outline-offset-2"
																@keydown=${(event: KeyboardEvent) => event.stopPropagation()}
																@click=${() => {
																	this.hidden = true
																	windowManager.updateHidden(this.id, true)
																	const next = [...this.cornerMembers]
																		.filter(member => member.id !== this.id)
																		.sort((a, b) => b.zIndex - a.zIndex)[0]
																	if (next) windowManager.activate(next.id)
																	this.dispatchScopedEvent('window-hide', { id: this.id }, { bubbles: true })
																}}
															>
																<span aria-hidden="true">×</span>
															</button>`,
													)}
												</div>
											`,
										)}
									</div>
								</schmancy-scroll>
								<div class="flex h-full shrink-0 items-center" @keydown=${(event: Event) => event.stopPropagation()}>
									<slot name="header-actions"></slot>
								</div>
							`,
							() => html`
								<div class="flex-1 min-w-0">
									<slot></slot>
								</div>
								<svg
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									class="shrink-0 text-surface-on/40 transition-transform duration-200 ${this.open ? 'rotate-180' : ''}"
									aria-hidden="true"
								>
									<path
										d="M6 9L12 15L18 9"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							`,
						)}
					</div>
				</section>
			</schmancy-surface>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-window': SchmancyWindow
	}
}
